import { useEffect, useMemo, useState } from "react";
import { Plus, X, PackagePlus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { ProductPicker } from "@/components/ProductPicker";
import { ProductDialog } from "@/components/ProductDialogs";
import { ComboSelect } from "@/components/ComboSelect";
import { useProducts } from "@/hooks/useData";
import { api, errMsg } from "@/lib/api";

const FIELDS = [["asin", "ASIN"], ["seller_sku", "Seller SKU"], ["group", "Product group"], ["category", "Category"], ["size", "Size / pattern"], ["bore", "Bore / grip"]];
const COMBO = ["group", "category"];
let seq = 0;
const key = () => `k-${++seq}`;

const RemoveBtn = ({ onClick, testid }) => (
  <button type="button" onClick={onClick} className="p-1 text-muted-foreground hover:text-red-400" data-testid={testid}><X className="h-4 w-4" /></button>
);

const LinkedRows = ({ comps, setComps, products, onNewProduct }) => {
  const upd = (i, k, v) => setComps(comps.map((c, j) => (j === i ? { ...c, [k]: v } : c)));
  return (
    <div className="space-y-2" data-testid="asin-linked-section">
      <Label className="block">Stock products <span className="font-normal text-muted-foreground">(stock is deducted when this ASIN sells)</span></Label>
      {comps.map((c, i) => (
        <div key={c._k} className="flex items-center gap-2">
          <div className="flex-1 min-w-0"><ProductPicker products={products} value={c.product_id} onChange={(id) => upd(i, "product_id", id)} testid={`asin-comp-product-${i}`} /></div>
          <Input type="number" min="1" value={c.qty} onChange={(e) => upd(i, "qty", e.target.value)} className="w-20 font-mono" data-testid={`asin-comp-qty-${i}`} />
          <RemoveBtn onClick={() => setComps(comps.filter((_, j) => j !== i))} testid={`asin-comp-remove-${i}`} />
        </div>
      ))}
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" size="sm" onClick={() => setComps([...comps, { _k: key(), product_id: "", qty: 1 }])} data-testid="asin-comp-add-btn"><Plus className="h-4 w-4" /> Add product</Button>
        <Button type="button" variant="outline" size="sm" onClick={onNewProduct} data-testid="asin-new-product-btn"><PackagePlus className="h-4 w-4" /> New product (not in Product Master)</Button>
      </div>
    </div>
  );
};

const ExtraRows = ({ extras, setExtras }) => {
  const upd = (i, k, v) => setExtras(extras.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  return (
    <div className="space-y-2" data-testid="asin-extra-section">
      <Label className="block">Other items in the box <span className="font-normal text-muted-foreground">(not tracked in stock)</span></Label>
      {extras.map((x, i) => (
        <div key={x._k} className="flex items-center gap-2">
          <Input value={x.name} placeholder="e.g. User manual, Allen key" onChange={(e) => upd(i, "name", e.target.value)} className="flex-1" data-testid={`asin-extra-name-${i}`} />
          <Input type="number" min="1" value={x.qty} onChange={(e) => upd(i, "qty", e.target.value)} className="w-20 font-mono" data-testid={`asin-extra-qty-${i}`} />
          <RemoveBtn onClick={() => setExtras(extras.filter((_, j) => j !== i))} testid={`asin-extra-remove-${i}`} />
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={() => setExtras([...extras, { _k: key(), name: "", qty: 1 }])} data-testid="asin-extra-add-btn"><Plus className="h-4 w-4" /> Add item</Button>
    </div>
  );
};

const initState = (asin) => ({
  f: Object.fromEntries(FIELDS.map(([k]) => [k, asin?.[k] || ""])),
  comps: (asin?.components?.length ? asin.components : [{ product_id: "", qty: 1 }]).map((c) => ({ _k: key(), product_id: c.product_id, qty: c.qty })),
  extras: (asin?.extra_contents || []).map((x) => ({ _k: key(), name: x.name, qty: x.qty })),
  note: asin?.contents_note || "",
});

export const AsinDialog = ({ open, onOpenChange, asin, onSaved, choices = {} }) => {
  const { products } = useProducts();
  const categories = useMemo(() => [...new Set(products.map((p) => p.category).filter(Boolean))], [products]);
  const [s, setS] = useState(initState(null));
  const [newProd, setNewProd] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setS(initState(asin)); }, [open, asin]);
  const patch = (k) => (v) => setS((p) => ({ ...p, [k]: typeof v === "function" ? v(p[k]) : v }));
  const onCreated = (p) => setS((st) => {
    const empty = st.comps.findIndex((c) => !c.product_id);
    const comps = empty >= 0 ? st.comps.map((c, i) => (i === empty ? { ...c, product_id: p.id } : c)) : [...st.comps, { _k: key(), product_id: p.id, qty: 1 }];
    return { ...st, comps };
  });
  const save = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!(s.f.group || "").trim()) return toast.error("Product group is required — pick one or type a new one");
    const body = { ...s.f, contents_note: s.note,
      components: s.comps.filter((c) => c.product_id && Number(c.qty) > 0).map((c) => ({ product_id: c.product_id, qty: Number(c.qty) })),
      extra_contents: s.extras.filter((x) => x.name.trim() && Number(x.qty) > 0).map((x) => ({ name: x.name.trim(), qty: Number(x.qty) })) };
    setBusy(true);
    try {
      if (asin) await api.put(`/asins/${asin.id}`, body); else await api.post("/asins", body);
      toast.success(asin ? "ASIN updated" : "ASIN added");
      onSaved();
      onOpenChange(false);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="asin-dialog">
        <DialogHeader>
          <DialogTitle>{asin ? `Edit ${asin.asin}` : "Add ASIN"}</DialogTitle>
          <DialogDescription>ASIN details and what goes inside 1 unit sold on Amazon.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="min-w-0 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {FIELDS.map(([k, label]) => (
              <div key={k} className="space-y-1.5"><Label>{label}</Label>
                {COMBO.includes(k)
                  ? <ComboSelect value={s.f[k]} options={choices[k] || []} onChange={(v) => patch("f")((f) => ({ ...f, [k]: v }))} testid={`asin-${k}-select`} />
                  : <Input required={k === "asin"} value={s.f[k]} onChange={(e) => patch("f")((f) => ({ ...f, [k]: e.target.value }))} data-testid={`asin-${k}-input`} />}
              </div>
            ))}
          </div>
          <div className="space-y-4 rounded-md border border-border p-3" data-testid="asin-contents-section">
            <div className="text-sm font-semibold">Contents per unit</div>
            <LinkedRows comps={s.comps} setComps={patch("comps")} products={products} onNewProduct={() => setNewProd(true)} />
            <ExtraRows extras={s.extras} setExtras={patch("extras")} />
            <div className="space-y-1.5"><Label>Contents note</Label>
              <Textarea rows={2} value={s.note} onChange={(e) => patch("note")(e.target.value)} placeholder="e.g. 1 barbell, 2 spring collars, 1 manual" data-testid="asin-contents-note-input" /></div>
          </div>
          <DialogFooter><Button type="submit" disabled={busy} data-testid="asin-save-dialog-btn">{busy ? "Saving…" : "Save"}</Button></DialogFooter>
        </form>
        <ProductDialog open={newProd} onOpenChange={setNewProd} product={null} categories={categories} onCreated={onCreated} />
      </DialogContent>
    </Dialog>
  );
};

export const ContentsCell = ({ a }) => {
  const extras = a.extra_contents || [];
  if (!a.components.length && !extras.length && !a.contents_note) return <span className="text-amber-500" data-testid={`asin-contents-empty-${a.asin}`}>No contents yet</span>;
  return (
    <div className="space-y-0.5" data-testid={`asin-contents-${a.asin}`}>
      {a.components.map((c) => (
        <div key={c.product_id} className={c.active ? "" : "text-red-400"}><span className="font-mono">{c.qty}×</span> {c.name}{!c.active && " (removed)"}</div>
      ))}
      {extras.map((x, i) => (
        <div key={`${x.name}-${i}`} className="text-muted-foreground"><span className="font-mono">{x.qty}×</span> {x.name} <span className="text-[10px] uppercase tracking-wider">no stock</span></div>
      ))}
      {a.contents_note && <div className="italic text-muted-foreground whitespace-pre-line">{a.contents_note}</div>}
    </div>
  );
};
